import React, { useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faExternalLinkAlt,
  faEdit,
  faSpinner,
  faSave,
} from "@fortawesome/free-solid-svg-icons";
import { useAuthUserData } from "hooks/useAuthUserData/useAuthUserData";
import { constants, generateRoutePath } from "utils";
import { colors } from "utils";
import {
  CopyIconButton,
  HeaderRow,
  StatusPill,
  StandardsBadges,
  TriBoolValue,
  SectionCard,
  monoValueStyle,
  fmtDate,
} from "./displayPrimitives";
import {
  REVIEW_STATUS_OPTIONS,
  fetchCcssRelationships,
  saveReview,
  isEndpointMissing,
} from "./evaluatorApi";

// Metadata rows hidden when Blind Review is ON (SPEC §10.2b/§10.2c): anything
// that would reveal the evaluator's authored tags before the SME commits a
// judgment.
const BlindAware = ({ blind, children }) => (blind ? null : <>{children}</>);

const EndpointPending = ({ label }) => (
  <div style={{ fontSize: "0.85em", color: "#b06a00", fontStyle: "italic" }}>
    {label} endpoint not available yet — pending backend.
  </div>
);

const CcssRelationships = ({ questionId, blind }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!questionId) return undefined;
    setLoading(true);
    setPending(false);
    setError(false);
    fetchCcssRelationships(questionId)
      .then((res) => {
        if (!cancelled) setData(res);
      })
      .catch((e) => {
        if (cancelled) return;
        if (isEndpointMissing(e)) setPending(true);
        else setError(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [questionId]);

  if (loading) {
    return (
      <SectionCard title="CCSS Relationships">
        <FontAwesomeIcon icon={faSpinner} spin /> Loading relationships…
      </SectionCard>
    );
  }
  if (pending) {
    return (
      <SectionCard title="CCSS Relationships">
        <EndpointPending label="CCSS relationships" />
      </SectionCard>
    );
  }
  if (error) {
    return (
      <SectionCard title="CCSS Relationships">
        <div style={{ color: "#a33", fontSize: "0.85em" }}>Failed to load relationships.</div>
      </SectionCard>
    );
  }

  const relationships = data?.relationships || [];
  if (relationships.length === 0) {
    return (
      <SectionCard title="CCSS Relationships">
        <span style={{ color: "#aaa" }}>No CCSS relationships.</span>
      </SectionCard>
    );
  }

  const openLesson = (lessonId) => {
    const url = generateRoutePath(constants.EVALUATOR_REVIEW_LESSON_ROUTE_NAME, {
      lessonId,
    });
    window.open(url, "_blank", "noopener");
  };

  return (
    <SectionCard title="CCSS Relationships">
      <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
        {relationships.map((rel) => (
          <div
            key={rel.ccss_code}
            style={{
              border: "1px solid #eee",
              borderRadius: "6px",
              padding: "10px 12px",
              background: "#fafbfc",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "8px" }}>
              <StandardsBadges standards={[rel.ccss_code]} />
              <span style={{ fontSize: "0.9em", color: "#333" }}>{rel.ccss_name}</span>
              <CopyIconButton value={rel.ccss_code} title="Copy CCSS code" />
            </div>

            <BlindAware blind={blind}>
              <div style={{ marginBottom: "8px" }}>
                <div
                  style={{
                    textTransform: "uppercase",
                    fontSize: "0.65em",
                    fontWeight: 700,
                    letterSpacing: "0.05em",
                    color: "#999",
                    marginBottom: "4px",
                  }}
                >
                  Learning Components
                </div>
                {(rel.learning_components || []).length === 0 ? (
                  <span style={{ color: "#aaa", fontSize: "0.85em" }}>—</span>
                ) : (
                  <ul style={{ margin: 0, paddingLeft: "16px" }}>
                    {rel.learning_components.map((lc) => (
                      <li key={lc.lc_id} style={{ fontSize: "0.88em" }}>
                        <span style={monoValueStyle}>{lc.lc_id}</span>
                        <CopyIconButton value={lc.lc_id} title="Copy LC ID" />
                        {" — "}
                        {lc.lc_name}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </BlindAware>

            <div>
              <div
                style={{
                  textTransform: "uppercase",
                  fontSize: "0.65em",
                  fontWeight: 700,
                  letterSpacing: "0.05em",
                  color: "#999",
                  marginBottom: "4px",
                }}
              >
                IM360 Lessons
              </div>
              {(rel.im_lessons || []).length === 0 ? (
                <span style={{ color: "#aaa", fontSize: "0.85em" }}>—</span>
              ) : (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "4px",
                    maxHeight: "320px",
                    overflowY: "auto",
                    paddingRight: "4px",
                  }}
                >
                  {rel.im_lessons.map((lesson) => {
                    // Compact display: lesson_code + lesson name (e.g.
                    // "5.01.05 — A Partial-Quotients Algorithm"). No UUID. Grade
                    // / unit / lesson context is carried by the code + names.
                    const lessonName = lesson.lesson_name || lesson.kg_identifier || "";
                    const label = lesson.lesson_code
                      ? `${lesson.lesson_code}${lessonName ? ` — ${lessonName}` : ""}`
                      : lessonName || `Unit ${lesson.unit_name} / Lesson ${lesson.lesson_name}`;
                    return (
                      <button
                        key={lesson.lesson_id}
                        onClick={() => openLesson(lesson.lesson_id)}
                        title="Open lesson detail (description, LCs, cooldown problems) in a new tab"
                        style={{
                          textAlign: "left",
                          background: "white",
                          border: `1px solid ${colors.blue200}`,
                          borderRadius: "6px",
                          padding: "6px 10px",
                          cursor: "pointer",
                          fontSize: "0.88em",
                          color: colors.blue700,
                        }}
                      >
                        <FontAwesomeIcon icon={faExternalLinkAlt} style={{ fontSize: "0.8em" }} />
                        &nbsp;{label}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </SectionCard>
  );
};

const SmeReviewBlock = ({ questionId, initialReview }) => {
  const { authUserData } = useAuthUserData();
  const [status, setStatus] = useState(initialReview?.status || "pending");
  const [notes, setNotes] = useState(initialReview?.notes || "");
  const [statusUpdatedAt, setStatusUpdatedAt] = useState(initialReview?.status_updated_at || null);
  const [notesUpdatedAt, setNotesUpdatedAt] = useState(initialReview?.notes_updated_at || null);
  const [saving, setSaving] = useState(false);
  const [pending, setPending] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [savedFlash, setSavedFlash] = useState(false);

  useEffect(() => {
    setStatus(initialReview?.status || "pending");
    setNotes(initialReview?.notes || "");
    setStatusUpdatedAt(initialReview?.status_updated_at || null);
    setNotesUpdatedAt(initialReview?.notes_updated_at || null);
    setPending(false);
    setSaveError("");
  }, [questionId, initialReview]);

  const handleSave = async () => {
    setSaving(true);
    setSaveError("");
    setSavedFlash(false);
    try {
      const updated = await saveReview(questionId, { status, notes });
      setStatus(updated?.status ?? status);
      setNotes(updated?.notes ?? notes);
      setStatusUpdatedAt(updated?.status_updated_at ?? new Date().toISOString());
      setNotesUpdatedAt(updated?.notes_updated_at ?? new Date().toISOString());
      setSavedFlash(true);
    } catch (e) {
      if (isEndpointMissing(e)) setPending(true);
      else setSaveError("Failed to save review.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <SectionCard title="SME Review" style={{ background: "#fbfcff" }}>
      <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
        <HeaderRow label="SME">
          <span>{authUserData?.email || "—"}</span>
        </HeaderRow>

        <HeaderRow label="Review Status">
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            disabled={saving}
            style={{ padding: "4px 8px", borderRadius: "4px", border: "1px solid #ccc" }}
          >
            {REVIEW_STATUS_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </HeaderRow>
        <HeaderRow label="Status updated">
          <span style={{ fontSize: "0.85em", color: "#888" }}>{fmtDate(statusUpdatedAt)}</span>
        </HeaderRow>

        <div>
          <div
            style={{
              textTransform: "uppercase",
              fontSize: "0.68em",
              fontWeight: 600,
              letterSpacing: "0.05em",
              color: "#8a8a8a",
              marginBottom: "4px",
            }}
          >
            Notes
          </div>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            disabled={saving}
            rows={4}
            placeholder="SME notes…"
            style={{
              width: "100%",
              padding: "8px 10px",
              borderRadius: "6px",
              border: "1px solid #ccc",
              resize: "vertical",
              fontFamily: "inherit",
            }}
          />
          <div style={{ fontSize: "0.8em", color: "#888", marginTop: "2px" }}>
            Notes updated: {fmtDate(notesUpdatedAt)}
          </div>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <button
            onClick={handleSave}
            disabled={saving}
            style={{
              background: colors.blue500,
              color: "white",
              border: "none",
              borderRadius: "20px",
              padding: "8px 16px",
              cursor: saving ? "not-allowed" : "pointer",
              opacity: saving ? 0.6 : 1,
              fontSize: "14px",
            }}
          >
            {saving ? <FontAwesomeIcon icon={faSpinner} spin /> : <FontAwesomeIcon icon={faSave} />}
            &nbsp;Save review
          </button>
          {savedFlash && <span style={{ color: colors.green600, fontSize: "0.85em" }}>Saved.</span>}
          {saveError && <span style={{ color: "#a33", fontSize: "0.85em" }}>{saveError}</span>}
        </div>
        {pending && <EndpointPending label="Review-save" />}
      </div>
    </SectionCard>
  );
};

export const EvaluatorQuestionDetail = ({ challenge, blindReview, onEdit }) => {
  if (!challenge) {
    return (
      <div style={{ color: "#999", padding: "24px" }}>
        Select a question to see its metadata and review controls.
      </div>
    );
  }

  const openFullView = () => {
    const url = generateRoutePath(constants.EVALUATOR_REVIEW_FULL_VIEW_ROUTE_NAME, {
      id: challenge.id,
    });
    window.open(url, "_blank", "noopener");
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
      <SectionCard title="Question metadata">
        <div style={{ display: "flex", flexDirection: "column", gap: "7px" }}>
          <HeaderRow label="Question ID">
            <span style={monoValueStyle}>{challenge.id}</span>
            <CopyIconButton value={challenge.id} title="Copy OKO question ID" />
          </HeaderRow>
          <HeaderRow label="Status">
            <StatusPill status={challenge.status} />
          </HeaderRow>
          <HeaderRow label="CCSS">
            <StandardsBadges standards={challenge.standards} />
          </HeaderRow>
          <HeaderRow label="Type">
            <span>{challenge.question_type || (challenge.answer_options ? "multiplechoice" : "openresponse")}</span>
          </HeaderRow>

          <BlindAware blind={blindReview}>
            <HeaderRow label="LC ID">
              {challenge.lc_id ? (
                <>
                  <span style={monoValueStyle}>{challenge.lc_id}</span>
                  <CopyIconButton value={challenge.lc_id} title="Copy LC ID" />
                </>
              ) : (
                <span style={{ color: "#aaa" }}>untagged</span>
              )}
            </HeaderRow>
            <HeaderRow label="IM Lesson">
              {challenge.im_lesson_id ? (
                <>
                  <span style={monoValueStyle}>{challenge.im_lesson_id}</span>
                  <CopyIconButton value={challenge.im_lesson_id} title="Copy IM lesson ID" />
                </>
              ) : (
                <span style={{ color: "#aaa" }}>untagged</span>
              )}
            </HeaderRow>
            <HeaderRow label="Source">
              <span>{challenge.source || "—"}</span>
            </HeaderRow>
            <HeaderRow label="External ID">
              {challenge.external_id ? (
                <>
                  <span style={monoValueStyle}>{challenge.external_id}</span>
                  <CopyIconButton value={challenge.external_id} title="Copy external ID" />
                </>
              ) : (
                <span style={{ color: "#aaa" }}>—</span>
              )}
            </HeaderRow>
            <HeaderRow label="Word problem">
              <TriBoolValue value={challenge.word_problem} />
            </HeaderRow>
            <HeaderRow label="Illustration">
              <TriBoolValue value={challenge.illustration} />
            </HeaderRow>
            <HeaderRow label="Equation-centered">
              <TriBoolValue value={challenge.equation_centered} />
            </HeaderRow>
          </BlindAware>

          <HeaderRow label="Created">
            <span style={{ fontSize: "0.85em", color: "#888" }}>{fmtDate(challenge.created_at)}</span>
          </HeaderRow>
          <HeaderRow label="Updated">
            <span style={{ fontSize: "0.85em", color: "#888" }}>{fmtDate(challenge.updated_at)}</span>
          </HeaderRow>
        </div>

        <div style={{ display: "flex", gap: "8px", marginTop: "12px" }}>
          <button
            onClick={openFullView}
            style={{
              background: "white",
              border: `1px solid ${colors.nightlyWoods500}`,
              color: colors.nightlyWoods500,
              borderRadius: "20px",
              padding: "6px 14px",
              cursor: "pointer",
              fontSize: "13px",
            }}
          >
            <FontAwesomeIcon icon={faExternalLinkAlt} />
            &nbsp;Full View
          </button>
          {onEdit && (
            <button
              onClick={() => onEdit(challenge)}
              style={{
                background: "white",
                border: `1px solid ${colors.nightlyWoods500}`,
                color: colors.nightlyWoods500,
                borderRadius: "20px",
                padding: "6px 14px",
                cursor: "pointer",
                fontSize: "13px",
              }}
            >
              <FontAwesomeIcon icon={faEdit} />
              &nbsp;Edit
            </button>
          )}
        </div>
      </SectionCard>

      <CcssRelationships questionId={challenge.id} blind={blindReview} />

      <SmeReviewBlock
        questionId={challenge.id}
        initialReview={{
          status: challenge.review_status,
          notes: challenge.review_notes,
          status_updated_at: challenge.review_status_updated_at,
          notes_updated_at: challenge.review_notes_updated_at,
        }}
      />
    </div>
  );
};
